import { useParams } from 'react-router-dom'

import { Table } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import hasFeature from 'modules/core/lib/hasFeature'

import Body from 'modules/bookkeepers/components/TableComponents/Body'
import Header from 'modules/bookkeepers/components/TableComponents/Header'
import { IncidenceQueryData } from 'modules/bookkeepers/hooks/IncidencesContext'
import { useIncidenceFilterContext } from 'modules/bookkeepers/lib/IncidenceFilterContext'
import {
  getIncidenceTypeNavigationMapping,
  incidenceTypeNavigation,
} from 'modules/bookkeepers/lib/incidenceTypeNavigation'
import { useGetMarketplaceCompanyInstallations } from 'modules/marketplace/hooks/integrations/companyInstallations/useGetMarketplaceCompanyInstallations'
import { useGetMarketplaceIntegrations } from 'modules/marketplace/hooks/integrations/useGetMarketplaceIntegrations'
import integrationCodes from 'modules/marketplace/lib/integrationCodes'
import { useInhousePayroll } from 'modules/payroll_bundle/hooks/useInhousePayroll'
import {
  INCIDENCE_STATUSES,
  INHOUSE_INCIDENCE_STATUSES,
} from 'modules/payroll_bundle/lib/Incidences/Status'

interface Props {
  incidences: IncidenceQueryData[]
  isLoading: boolean
  isMessageTab: boolean
  selectedIncidencesId: Set<number>
  setSelectedIncidencesId: (selectedIncidencesId: Set<number>) => void
}

const IncidencesTable = ({
  incidences,
  isLoading,
  isMessageTab,
  selectedIncidencesId,
  setSelectedIncidencesId,
}: Props) => {
  const { hasInhousePayroll } = useInhousePayroll()
  const { id } = useParams()
  const openedIncidenceId = Number(id)
  const { navigation } = useNavigation()
  const { filters } = useIncidenceFilterContext()
  const hasDevCompensations = hasFeature('dev_compensations')

  useUnsafeEffect(() => {
    setSelectedIncidencesId(new Set())
  }, [isMessageTab, setSelectedIncidencesId])

  const selectableStatuses: string[] = hasInhousePayroll
    ? [
        INHOUSE_INCIDENCE_STATUSES.to_do,
        INHOUSE_INCIDENCE_STATUSES.doing,
        INHOUSE_INCIDENCE_STATUSES.done,
        INHOUSE_INCIDENCE_STATUSES.discarded,
      ]
    : [INCIDENCE_STATUSES.in_preparation]

  const handleRowSelectPath = (selectedId: number, isReopened: boolean) => {
    const incidencesNoPolicyPath = hasDevCompensations
      ? navigation.compensations.employeeUpdates
      : navigation.payrollBundle.incidences

    const incidenceInformationNoPolicyPath = incidencesNoPolicyPath.id.fullPath({ id: selectedId })
    const incidenceUpdateNoPolicyPath = incidencesNoPolicyPath.idChanges.fullPath({
      id: selectedId,
    })

    return isReopened ? incidenceUpdateNoPolicyPath : incidenceInformationNoPolicyPath
  }

  const handleRowSelectPathForMessages = (selectedIncidenceId: number, _isReopened: boolean) => {
    return hasDevCompensations
      ? navigation.compensations.employeeUpdates.idMessages.fullPath({ id: selectedIncidenceId })
      : navigation.payrollBundle.incidences.idMessages.fullPath({ id: selectedIncidenceId })
  }

  const selectableIncidences = incidences.filter((incidence) =>
    selectableStatuses.includes(incidence.status)
  )

  const selectableIncidenceIds = new Set(selectableIncidences.map((incidence) => incidence.id))

  const onToggleIncidence = (incidenceId: number) => {
    if (selectedIncidencesId.has(incidenceId)) {
      selectedIncidencesId.delete(incidenceId)
    } else {
      selectedIncidencesId.add(incidenceId)
    }

    setSelectedIncidencesId(new Set(selectedIncidencesId))
  }

  const onToggleAll = () => {
    const newSelectedIncidences: Set<number> =
      selectedIncidencesId.size > 0 ? new Set() : selectableIncidenceIds
    setSelectedIncidencesId(newSelectedIncidences)
  }

  const { data: integrationsData, loading: loadingIntegration } = useGetMarketplaceIntegrations({
    datoIds: [integrationCodes.a3innuva],
  })

  const { data: companyInstallations } = useGetMarketplaceCompanyInstallations(
    {
      // FIXME: We should arrange the components to call this hook only when there is an id
      integrationIds: [integrationsData.at(0)?.id || 0],
    },
    false
  )

  const showIncidencePayrollIntegrationStatus = () => {
    if (companyInstallations.length > 0) {
      const mapping = getIncidenceTypeNavigationMapping()

      const hirings = mapping[incidenceTypeNavigation.NEW_HIRES]?.[0]
      const terminations = mapping[incidenceTypeNavigation.TERMINATIONS]?.[0]
      return filters.name === hirings || filters.name === terminations
    } else {
      return false
    }
  }

  return (
    <Table autoLayout fullWidth>
      <Header.Admin
        messageTab={isMessageTab}
        onToggleAll={onToggleAll}
        totalSelectableIncidences={selectableIncidenceIds.size}
        totalSelectedIncidences={selectedIncidencesId.size}
        showIncidencePayrollIntegrationStatus={showIncidencePayrollIntegrationStatus()}
      />
      <Body
        selectedIncidence={openedIncidenceId}
        selectableIncidenceIds={selectableIncidenceIds}
        selectedIncidencesId={selectedIncidencesId}
        onToggleIncidence={onToggleIncidence}
        loading={isLoading || loadingIntegration}
        onRowSelectPath={isMessageTab ? handleRowSelectPathForMessages : handleRowSelectPath}
        showIncidencePayrollIntegrationStatus={showIncidencePayrollIntegrationStatus()}
      />
    </Table>
  )
}

export default observer(IncidencesTable)
